import { Box, InputAdornment } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Plus } from '@styled-icons/bootstrap/Plus';
import { Search } from '@styled-icons/bootstrap/Search';
import { Close } from '@styled-icons/evaicons-solid/Close';
import Loader from 'Components/Forms/Loader';
import { SearchInput } from 'Components/Forms/SearchToolbar';
import PartnerDeleteDialog from 'FeatureModules/Partner/Components/PartnerDeleteDialog/PartnerDeleteDialog';
import { CloseButton } from 'FeatureModules/Partner/Components/Partners.styles';
import PartnersTable from 'FeatureModules/Partner/Components/PartnersTable/PartnersTable';
import { EPartnerHeaderColumnEnum } from 'FeatureModules/Partner/Components/PartnersTable/partnerHeaderColumns';
import PartnerSkeleton from 'FeatureModules/Partner/Components/Skeleton/PartnerSkeleton';
import { usePartnerMutations } from 'FeatureModules/Partner/Components/hooks';
import usePartners from 'FeatureModules/Partner/Components/hooks/usePartners';
import { EmptyStateCard, Tabs } from 'DesignSystem';
import ThemeButton from 'DesignSystem/Buttons/ThemeButton/ThemeButton';
import { ITab } from 'DesignSystem/Primitives/Tabs/Tabs';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { EBackgroundTypes, EItemTypes } from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import usePageTitle from '../../../hooks/usePageTitle';
import Filter from 'Components/Filter/Filter';
import ActionContainer from 'Components/ActionContainer';
import EmptySearchResults from 'Components/EmptySearchResults';
import ErrorState from 'Components/ErrorState';
import InfiniteScrollWrapper from 'Components/InfiniteScrollWrapper';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';

const PARTNER_TABS: ITab[] = [
  {
    key: 'suppliers',
    label: 'Suppliers',
  },
];

const Container = styled('div')(() => ({
  position: 'relative',
  minHeight: 50,

  '& .MuiGrid-item': {
    display: 'flex',
  },
}));

const Partners: FC = () => {
  usePageTitle('Partners');

  const {
    error,
    hasNextPage,
    partners,
    searchTerm,
    partnerToDelete,
    loading,
    filters,
    handleFilterChange,
    handleFilterReset,
    handlePageEndReached,
    handleSearchTermChange,
    handleCreatePartner,
    onDeletePartner,
    onEditPartner,
    clearDeletingPartner,
  } = usePartners({ useInfiniteScroll: true, useSearchQuery: true });

  const { handleRemovePartner } = usePartnerMutations();
  const navigate = useNavigate();

  const onSelectPartner = (partner: IBasePartnerCompany) => {
    navigate(`/partners/${partner.id}`);
  };

  if (error) {
    return <ErrorState />;
  }

  return (
    <PageContainer>
      <PageTitle title="Partners" />
      <Box mt={3} mb={1}>
        {/* TODO: New partner types will come soon like Buyer */}
        <Tabs tabs={PARTNER_TABS} selectedTab="suppliers" onChange={() => undefined} />
      </Box>
      <Filter
        filters={filters}
        onFilterChange={handleFilterChange}
        onFilterReset={handleFilterReset}
      />
      <ActionContainer>
        <SearchInput
          setDebouncedState={handleSearchTermChange}
          className="search-input"
          data-testid="partner-search-input"
          delay={500}
          placeholder="Search partner"
          value={searchTerm}
          inputProps={{
            endAdornment: (
              <InputAdornment position="start">
                {searchTerm === '' ? (
                  <Search size={12} />
                ) : (
                  <CloseButton onClick={() => handleSearchTermChange('')}>
                    <Close size={16} />
                  </CloseButton>
                )}
              </InputAdornment>
            ),
          }}
        />
        <Box ml={1}>
          <ThemeButton
            size="medium"
            color="PRIMARY"
            onClick={handleCreatePartner}
            startIcon={<Plus size={20} color="white" />}
            data-testid="add-new-partner-btn"
            loading={false}
          >
            Add new partner
          </ThemeButton>
        </Box>
      </ActionContainer>

      {loading && <Loader relativeHeightToScreen />}

      {!loading && (partners.length > 0 || (searchTerm && searchTerm.length > 0)) && (
        <Container>
          {partners.length > 0 && (
            <InfiniteScrollWrapper hasMore={hasNextPage} next={handlePageEndReached}>
              <PartnersTable
                disablePagination
                partners={partners}
                onSelect={onSelectPartner}
                onEdit={onEditPartner}
                onDelete={onDeletePartner}
                columns={[
                  EPartnerHeaderColumnEnum.TITLE,
                  EPartnerHeaderColumnEnum.SITES_COUNT,
                  EPartnerHeaderColumnEnum.MODIFIED_TIMESTAMP,
                  EPartnerHeaderColumnEnum.ACTIONS,
                ]}
              />
            </InfiniteScrollWrapper>
          )}
        </Container>
      )}

      {!loading &&
        !partners.length &&
        partners.length === 0 &&
        searchTerm &&
        searchTerm.length > 0 && (
          <EmptySearchResults
            message="No search results found"
            action={{
              label: 'Create partner',
              onClick: handleCreatePartner,
            }}
            skeletonComponent={<PartnerSkeleton title={searchTerm || ''} />}
          />
        )}

      {!loading && partners.length === 0 && (!searchTerm || searchTerm?.length === 0) && (
        <Container>
          <Box mt={4}>
            <EmptyStateCard
              onCreateItemClick={handleCreatePartner}
              variant={EItemTypes.PARTNER}
              title="Add your partners to manage and communicate your supply chain"
              size={EAvailableSizes.LARGE}
              data-testid="partners-getting-started-card"
              background={EBackgroundTypes.WORLD}
              buttonText="Add partner"
              data-testid-button="partners-getting-started-card-button"
            />
          </Box>
        </Container>
      )}

      {partnerToDelete && (
        <PartnerDeleteDialog
          onClose={clearDeletingPartner}
          open={partnerToDelete !== undefined}
          partner={partnerToDelete}
          onSubmit={() => {
            handleRemovePartner({ companyId: partnerToDelete.id });
            clearDeletingPartner();
          }}
        />
      )}
    </PageContainer>
  );
};

export default Partners;
